// Hardened PostHog options, kept in a posthog-free module so the
// regression test can assert them without a DOM/SDK. Posture for a
// screening product:
//   * EU region only.
//   * person_profiles "identified_only" — anonymous unless we
//     explicitly identify with an opaque account id (never email).
//   * autocapture OFF and session recording OFF — element text on a
//     compliance dashboard could be a customer/entity name; we only
//     send sanitised pageviews + explicit events.
//   * sanitize_properties strips query strings (the screening query
//     never reaches the SPA, but this is a structural guard).
//   * NO externally loaded code. The SDK is bundled by Vite, but
//     posthog-js lazily injects <script> tags from the assets host for
//     its optional extensions (web vitals, dead clicks, exception
//     autocapture, remote-config.js, …) whenever the PROJECT-side
//     remote config turns one on. The portal's CSP is `script-src
//     'self'` (portal/app/public/_headers), so those loads are blocked
//     at the browser. capture_performance:false kills the web-vitals
//     bundle at the source, and disable_external_dependency_loading
//     makes the whole class impossible — remote config still arrives,
//     over the JSON endpoint that connect-src already allows.
function envStr(key: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  return v && v.length > 0 ? v : "";
}

export function posthogKey(): string {
  return envStr("VITE_POSTHOG_KEY");
}

export function posthogHost(): string {
  return envStr("VITE_POSTHOG_HOST") || "https://eu.i.posthog.com";
}

function stripQuery(u: string): string {
  return u.split("?")[0] ?? u;
}

export function sanitizeProperties(
  props: Record<string, unknown>,
): Record<string, unknown> {
  for (const k of [
    "$current_url",
    "$referrer",
    "$initial_current_url",
    "$pathname",
  ]) {
    const v = props[k];
    if (typeof v === "string") props[k] = stripQuery(v);
  }
  delete props.$ip;
  return props;
}

export interface HardenedPosthogOptions {
  api_host: string;
  ui_host: string;
  person_profiles: "identified_only";
  autocapture: false;
  disable_session_recording: true;
  disable_surveys: true;
  advanced_disable_feature_flags: true;
  capture_performance: false;
  disable_external_dependency_loading: true;
  capture_pageview: true;
  capture_pageleave: true;
  sanitize_properties: (
    props: Record<string, unknown>,
  ) => Record<string, unknown>;
}

export function posthogOptions(): HardenedPosthogOptions {
  return {
    api_host: posthogHost(),
    ui_host: "https://eu.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    disable_session_recording: true,
    disable_surveys: true,
    advanced_disable_feature_flags: true,
    capture_performance: false,
    disable_external_dependency_loading: true,
    capture_pageview: true,
    capture_pageleave: true,
    sanitize_properties: sanitizeProperties,
  };
}
